import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { fmtBytes, fmtDate, fmtMoney } from "../lib/format";
import { friendlyError, reportError } from "../lib/errorReporter";
import StatusBadge from "../components/StatusBadge";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Stable axios instance for the public route. Kept at module scope so its
// identity does not change between renders — that would defeat hook memoization.
const publicApi = axios.create({ baseURL: `${BASE}/api/public`, timeout: 30000 });

const EMPTY_FORM = {
  customer_name: "",
  message: "",
  decline_reason: "other",
  callback_requested: false,
};

export default function PublicQuotation() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // 'accepted' | 'question' | 'declined'
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await publicApi.get(`/q/${token}`);
      setData(r.data);
    } catch (err) {
      // Never log the public token — sanitised reporter drops it.
      reportError("publicQuotation.load", err);
      setError(err?.response?.status === 404 ? "not_found" : "error");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = useCallback(
    async (type) => {
      setBusy(true);
      try {
        const idem = crypto.randomUUID();
        await publicApi.post(
          `/q/${token}/response`,
          {
            response_type: type,
            customer_name: form.customer_name,
            message: form.message,
            decline_reason: type === "declined" ? form.decline_reason : null,
            // Only the question form offers the callback tick: an accept or a
            // decline never carries one, and no earlier choice is inherited.
            callback_requested: type === "question" ? form.callback_requested : false,
          },
          { headers: { "Idempotency-Key": idem } },
        );
        setDone(type);
        setModal(null);
        // This response is finished: the next one starts from a clean form, so a
        // requested callback can never travel with a later action.
        setForm(EMPTY_FORM);
        await load();
      } catch (err) {
        reportError("publicQuotation.submit", err);
        setError(friendlyError(err, "error"));
      } finally {
        setBusy(false);
      }
    },
    [token, form, load],
  );

  // Every dialog opens a NEW response, so it always starts from the empty form:
  // the previous message, decline reason and callback tick are never reused.
  const openModal = useCallback((type) => {
    setForm(EMPTY_FORM);
    setModal(type);
  }, []);

  const downloadFile = useCallback(
    (fid) => {
      window.open(`${BASE}/api/public/q/${token}/file/${fid}`, "_blank", "noopener,noreferrer");
    },
    [token],
  );

  if (error === "not_found") {
    return (
      <div className="qw-public">
        <div className="wrap">
          <div className="card qw-empty">{t("common.unauthorized")}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="qw-public">
        <div className="wrap">
          <div className="card qw-empty">{t("common.loading")}</div>
        </div>
      </div>
    );
  }

  const q = data.quotation;
  const org = data.organization;
  const canRespond = !data.is_terminal;
  const isExpired = data.is_expired;
  const allowedActions = isExpired ? ["question"] : ["accepted", "question", "declined"];

  return (
    <div className="qw-public">
      <div className="wrap">
        <div className="header">
          <div className="logo" aria-hidden="true">
            {org?.logo_url ? <img src={org.logo_url} alt="" /> : org?.name?.[0] || "Q"}
          </div>
          <div className="qw-public-org">
            <div style={{ fontSize: 12, color: "#64748b", letterSpacing: 2, textTransform: "uppercase" }}>
              {t("public.header_offer_from")}
            </div>
            <div className="qw-public-org-name" data-testid="public-company-name">{org?.name}</div>
            <div className="qw-public-org-contact" data-testid="public-company-contact">
              {[org?.phone, org?.website, org?.address].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="qw-public-head">
            <div className="main">
              <div style={{ fontSize: 12, color: "#64748b" }} className="qw-break">
                {t("public.reference")} · {q.reference_number}
              </div>
              <h1>{q.title}</h1>
              <div style={{ color: "#64748b" }} className="qw-break">{q.customer_name}</div>
            </div>
            <div className="aside">
              <div style={{ fontSize: 12, color: "#64748b" }}>{t("public.valid_until")}</div>
              <div style={{ fontWeight: 700 }}>{fmtDate(q.valid_until)}</div>
            </div>
          </div>
          <div className="row2">
            <div className="qw-card">
              <div className="card-title">{t("quotations.fields.value")}</div>
              <div className="card-value">{fmtMoney(q.value, q.currency)}</div>
            </div>
            <div className="qw-card">
              <div className="card-title">{t("common.status")}</div>
              <div style={{ marginTop: 6 }}>
                <StatusBadge status={q.status} />
              </div>
            </div>
          </div>

          {isExpired && (
            <div className="banner expired" data-testid="public-expired-banner">
              {t("public.expired_banner")}
            </div>
          )}
          {data.is_terminal && (
            <div className="banner" data-testid="public-terminal-banner">
              {q.status === "archived"
                ? t("public.archived_banner")
                : t("public.terminal_banner")}
            </div>
          )}
          {done && (
            <div
              className="banner"
              style={{ background: "#dcfce7", borderColor: "#bbf7d0", color: "#166534" }}
              data-testid="public-thanks"
            >
              {done === "accepted" && t("public.thanks_accepted")}
              {done === "declined" && t("public.thanks_declined")}
              {done === "question" && t("public.thanks_question")}
            </div>
          )}

          {data.files?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
                {t("quotations.original_file")}
              </div>
              <div className="qw-file-list" style={{ marginTop: 8 }}>
                {data.files.map((f) => (
                  <div key={f.id} className="qw-file-row">
                    <div className="qw-file-info">
                      <div className="qw-file-name" title={f.original_name}>{f.original_name}</div>
                      <div className="qw-file-meta">
                        {f.file_ext.toUpperCase()} · {fmtBytes(f.file_size)}
                      </div>
                    </div>
                    <button
                      className="qw-btn primary"
                      onClick={() => downloadFile(f.id)}
                      data-testid={`public-download-${f.id}`}
                    >
                      {t("common.signed_download")}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canRespond && (
            <div className="action-row">
              {allowedActions.includes("accepted") && (
                <button
                  className="btn-a"
                  onClick={() => openModal("accepted")}
                  data-testid="public-accept"
                >
                  {t("public.accept")}
                </button>
              )}
              {allowedActions.includes("question") && (
                <button
                  className="btn-q"
                  onClick={() => openModal("question")}
                  data-testid="public-question"
                >
                  {t("public.ask_question")}
                </button>
              )}
              {allowedActions.includes("declined") && (
                <button
                  className="btn-d"
                  onClick={() => openModal("declined")}
                  data-testid="public-decline"
                >
                  {t("public.decline")}
                </button>
              )}
            </div>
          )}

          <div style={{ marginTop: 16, fontSize: 11, color: "#94a3b8" }}>
            {t("public.not_qualified_signature")}
          </div>
        </div>

        <div
          style={{ marginTop: 16, textAlign: "center", fontSize: 12, color: "#94a3b8" }}
          data-testid="public-attribution"
        >
          {org?.name ? `${org.name} ${t("public.powered_by")}` : "OfferTrace"}
        </div>
      </div>

      {modal && (
        <div
          className="qw-modal-backdrop"
          onClick={() => setModal(null)}
        >
          <div
            className="qw-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="public-modal-title"
            data-testid="public-modal"
          >
            <div className="h" id="public-modal-title">
              {modal === "accepted" && t("public.confirm_accept_title")}
              {modal === "declined" && t("public.confirm_decline_title")}
              {modal === "question" && t("public.question_title")}
            </div>
            <div className="qw-field">
              <label htmlFor="public-name-input">
                {t("public.name")}{" "}
                <span style={{ color: "#94a3b8" }}>({t("public.optional")})</span>
              </label>
              <input
                id="public-name-input"
                className="qw-input"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                data-testid="public-modal-name"
              />
            </div>
            {modal === "declined" && (
              <div className="qw-field">
                <label htmlFor="public-reason-select">{t("public.reason")}</label>
                <select
                  id="public-reason-select"
                  className="qw-select"
                  value={form.decline_reason}
                  onChange={(e) => setForm({ ...form, decline_reason: e.target.value })}
                  data-testid="public-modal-reason"
                >
                  {["price", "timing", "other_supplier", "postponed", "missing_requirement", "other"].map((r) => (
                    <option key={r} value={r}>
                      {t(`public.reasons.${r}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="qw-field">
              <label htmlFor="public-message-input">
                {t("public.message")}{" "}
                <span style={{ color: "#94a3b8" }}>({t("public.optional")})</span>
              </label>
              <textarea
                id="public-message-input"
                className="qw-textarea"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="public-modal-message"
              />
            </div>
            {modal === "question" && (
              <label className="hstack" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  checked={form.callback_requested}
                  onChange={(e) => setForm({ ...form, callback_requested: e.target.checked })}
                  data-testid="public-modal-callback"
                />{" "}
                {t("public.request_callback")}
              </label>
            )}
            <div className="footer">
              <button className="qw-btn ghost" onClick={() => setModal(null)} data-testid="public-modal-cancel">
                {t("common.cancel")}
              </button>
              <button
                className="qw-btn primary"
                onClick={() => submit(modal)}
                disabled={
                  busy ||
                  (modal === "question" &&
                    !form.message.trim() &&
                    !form.callback_requested)
                }
                data-testid="public-modal-submit"
              >
                {busy ? t("common.loading") : t("public.submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
